function initializeInfiniteScroll(root = document) {
  const isContainer = root !== document && root.matches && root.matches('[data-infinite-scroll]');
  const containers = isContainer ? [root] : Array.from(root.querySelectorAll('[data-infinite-scroll]'));

  containers.forEach((container) => {
    if (container.dataset.infiniteScrollReady === 'true') return;

    const grid = container.querySelector('#product-grid');
    const paginationWrapper = container.querySelector('[data-infinite-scroll-pagination]');
    const statusEl = container.querySelector('[data-infinite-scroll-status]');
    const spinnerEl = container.querySelector('[data-infinite-scroll-spinner]');
    const doneEl = container.querySelector('[data-infinite-scroll-done]');
    const errorEl = container.querySelector('[data-infinite-scroll-error]');
    const liveEl = container.querySelector('[data-infinite-scroll-live]');
    const retryButton = container.querySelector('[data-infinite-scroll-retry]');

    if (!grid || !paginationWrapper) return;

    const sectionId = grid.dataset.id;
    const collectionUrl = container.dataset.collectionUrl;
    let currentPage = parseInt(container.dataset.currentPage, 10) || 1;
    const totalPages = parseInt(container.dataset.totalPages, 10) || 1;

    if (!sectionId || !collectionUrl || currentPage >= totalPages) return;

    container.dataset.infiniteScrollReady = 'true';

    let isLoading = false;
    let isDone = false;
    let observer = null;

    const announce = (message) => {
      if (liveEl) liveEl.textContent = message;
    };

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.height = '1px';
    // base.css hides `div:empty` with display: none, which would stop
    // IntersectionObserver from ever seeing this element intersect.
    sentinel.innerHTML = '&nbsp;';

    function onScrollFallback() {
      const rect = sentinel.getBoundingClientRect();
      const triggerDistance = window.innerHeight + 600;
      if (rect.top <= triggerDistance) loadNextPage();
    }

    const debouncedScrollFallback = (() => {
      let timeoutId;
      return () => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(onScrollFallback, 150);
      };
    })();

    function stopWatching() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      window.removeEventListener('scroll', debouncedScrollFallback);
      window.removeEventListener('resize', debouncedScrollFallback);
    }

    function finish() {
      isDone = true;
      isLoading = false;
      stopWatching();
      sentinel.remove();
      if (spinnerEl) spinnerEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (doneEl) doneEl.hidden = false;
      announce(doneEl ? doneEl.textContent.trim() : 'No more products');
    }

    function loadNextPage() {
      if (isLoading || isDone) return;

      isLoading = true;
      if (spinnerEl) spinnerEl.hidden = false;
      if (errorEl) errorEl.hidden = true;
      announce('Loading more products');

      const nextPage = currentPage + 1;
      const url = `${collectionUrl}?section_id=${encodeURIComponent(sectionId)}&page=${nextPage}`;

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('#product-grid');

          if (!newGrid || newGrid.children.length === 0) {
            finish();
            return;
          }

          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((item) => fragment.appendChild(item));
          grid.appendChild(fragment);

          currentPage = nextPage;
          container.dataset.currentPage = String(currentPage);

          const updatedUrl = new URL(window.location.href);
          updatedUrl.searchParams.set('page', currentPage);
          window.history.replaceState(
            window.history.state,
            '',
            `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`
          );

          if (currentPage >= totalPages) {
            finish();
          } else {
            isLoading = false;
            if (spinnerEl) spinnerEl.hidden = true;
            announce('');
          }
        })
        .catch(() => {
          isLoading = false;
          if (spinnerEl) spinnerEl.hidden = true;
          if (errorEl) errorEl.hidden = false;
        });
    }

    paginationWrapper.classList.add('infinite-scroll-hidden');
    if (statusEl) statusEl.hidden = false;
    container.insertBefore(sentinel, statusEl || null);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
        },
        { rootMargin: '600px 0px 600px 0px' }
      );
      observer.observe(sentinel);
    } else {
      window.addEventListener('scroll', debouncedScrollFallback, { passive: true });
      window.addEventListener('resize', debouncedScrollFallback, { passive: true });
    }

    if (retryButton) {
      retryButton.addEventListener('click', () => {
        if (errorEl) errorEl.hidden = true;
        loadNextPage();
      });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeInfiniteScroll());
} else {
  initializeInfiniteScroll();
}

document.addEventListener('shopify:section:load', (event) => initializeInfiniteScroll(event.target));

const productGridContainer = document.getElementById('ProductGridContainer');
if (productGridContainer && 'MutationObserver' in window) {
  const gridReplaceObserver = new MutationObserver(() => initializeInfiniteScroll(productGridContainer));
  gridReplaceObserver.observe(productGridContainer, { childList: true });
}
